import {
  Container as RawContainer,
  InfoBlock as InfoBlockBase,
  size,
  mediaQueries,
  colors,
  typography,
  Button,
  Col,
  Row,
} from '@everlywell/leaves';
import SectionWrapper from 'components/SectionWrapper';
import { animated } from 'react-spring';
import styled, { css } from 'styled-components';

import RawSectionTitle from '../Enterprise/SectionTitle';

interface WrapperProps {
  backgroundColor?: 'Primary' | 'Secondary';
}
export const Wrapper = styled(SectionWrapper)<WrapperProps>`
  background-color: ${({ backgroundColor }) =>
    backgroundColor === 'Primary' ? colors.white : colors.teal1};
  background-image: ${(props) =>
    props.backgroundColor
      ? 'none'
      : `linear-Gradient(to top, ${colors.teal1} 60%, ${colors.white} 97%)`};
`;

export const Container = styled(RawContainer)`
  ${mediaQueries.forTabletHorizontalUp} {
    padding-bottom: ${size.xl2}px;
  }
`;

export const InfoBlock = styled(InfoBlockBase).attrs((props) => ({
  imageAlt: props.imageAlt,
}))`
  ${typography.subtitleSmall};

  ${mediaQueries.forDesktopUp} {
    ${typography.bodyText};
  }
`;

export const AnimatedBlockContainer = styled(animated.div)`
  margin-top: ${size.md}px;
  margin-bottom: ${size.md}px;
  div[class*='InfoBlock-'] {
    div:first-child {
      background-color: transparent;
    }
  }
`;

export const ColContainer = styled(Col)`
  width: 100%;

  ${mediaQueries.forTabletVerticalUp} {
    padding: 0;
  }
`;

export const InfoBlockContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  height: 100%;

  ${AnimatedBlockContainer} {
    &:last-child {
      ${InfoBlock} {
        margin-bottom: 0;
      }
    }
  }
`;

export const SectionTitle = styled(RawSectionTitle)`
  text-align: center;
  margin-bottom: ${size.lg}px;
`;

export const SectionHeadline = styled.h2`
  ${typography.h2Text};
  font-weight: normal !important;
  margin-bottom: ${size.md}px;
  text-align: center;

  ${mediaQueries.forTabletHorizontalUp} {
    margin: 0 auto;
    max-width: 881px;
    ${typography.h3Text};
  }
`;

export const Title = styled(RawSectionTitle)`
  text-align: left;
`;

export const Headline = styled.h2`
  ${typography.h2Text}
  text-align: left;
  margin-bottom: ${size.xs1}px;
`;

export const HeadlineDescription = styled.p`
  ${typography.bodyText};
  text-align: left;
  margin-top: ${size.xs1}px;
  margin-bottom: 0;

  ${mediaQueries.forTabletHorizontalUp} {
    margin-bottom: ${size.xl4}px;
  }
`;

const headlineDescriptionMarkdownStyle = css`
  margin: ${size.md}px auto 0 auto;

  a {
    color: ${colors.green4};
    text-decoration: none;

    :hover,
    :active {
      text-decoration: underline;
    }
  }
`;

export const HeadlineDescriptionMarkdownMobile = styled.p`
  ${typography.bodyTextSmall};
  ${headlineDescriptionMarkdownStyle};
  text-align: left;
  margin-bottom: ${size.md}px;

  a {
    font-weight: ${typography.weight.xbold};
  }

  ${mediaQueries.forDesktopUp} {
    display: none;
  }
`;

export const HeadlineDescriptionMarkdownDesktop = styled.p`
  ${typography.bodyText};
  ${headlineDescriptionMarkdownStyle};
  display: none;
  margin-bottom: ${size.lg}px;
  max-width: 670px;

  a {
    ${typography.buttonText};
  }

  ${mediaQueries.forDesktopUp} {
    display: block;
  }
`;

export const Copy = styled.p`
  ${typography.bodyText};
  text-align: left;
  margin-bottom: ${size.xl3}px;
  ${mediaQueries.forTabletHorizontalUp} {
    margin-bottom: 0;
  }
`;

export const ParallaxImagesCol = styled(Col)`
  order: 1;
  ${mediaQueries.forTabletHorizontalUp} {
    order: 0;
  }
`;

export const MainImageContainer = styled.div`
  z-index: -1;
  margin-top: ${size.xl5}px;
  margin-bottom: ${size.md}px;
  ${mediaQueries.forDesktopUp} {
    margin-top: ${size.xl4}px;
  }
`;

export const MobileAnimatedImage = styled(animated.div)`
  max-height: 412px;
  width: auto;
  ${mediaQueries.forTabletHorizontalUp} {
    display: none;
  }
`;

export const MobileAnimatedComponent = styled(animated.div)`
  max-height: 412px;
  width: auto;
  ${mediaQueries.forTabletHorizontalUp} {
    display: none;
  }
`;

export const MobileImageContainer = styled.div`
  position: relative;
  max-width: 365px;
  margin: 0 auto;
  ${mediaQueries.forTabletHorizontalUp} {
    display: none;
  }
`;

export const MobileOneImageContainer = styled.div`
  position: relative;
  max-width: 365px;
  margin: 0 auto;
  ${mediaQueries.forTabletHorizontalUp} {
    display: none;
  }
`;

export const DesktopImageContainer = styled.div`
  position: relative;
  display: none;
  ${mediaQueries.forTabletHorizontalUp} {
    display: block;
  }
`;

export const DesktopOneImageContainer = styled.div`
  position: relative;
  display: none;
  ${mediaQueries.forTabletHorizontalUp} {
    display: block;
  }
`;

export const TopImage = styled(animated.div)`
  position: absolute;
  top: 0;
  left: 30%;
  width: 100%;
  max-width: 245px;
  ${mediaQueries.forTabletHorizontalUp} {
    max-width: 294px;
    top: -2rem;
    left: unset;
    right: 3rem;
  }
`;

export const BottomImage = styled(animated.div)`
  position: absolute;
  left: 30%;
  top: 8rem;
  width: 100%;
  max-width: 245px;
  ${mediaQueries.forTabletHorizontalUp} {
    max-width: 517px;
    top: unset;
    bottom: -5rem;
    left: 0;
  }
`;

export const MiddleImage = styled.div`
  width: 100%;
  max-width: 530px;
  margin: 0 auto;
`;

export const Content = styled.div`
  margin-top: ${size.xl1}px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  ${mediaQueries.forTabletHorizontalUp} {
    margin-top: 0;
  }
`;

export const SubHeadline = styled.div`
  ${typography.h3Text};
  text-align: left;
`;

export const SubHeadlineDescription = styled.p`
  ${typography.bodyText};
  text-align: left;
  margin-top: ${size.xs1}px;
`;

export const MobileDisclaimer = styled.p`
  ${typography.bodyTextSmall};
  margin-top: ${size.xl1}px;
  margin-bottom: ${size.md}px;

  ${mediaQueries.forDesktopUp} {
    display: none;
  }
`;

export const DesktopDisclaimer = styled.p`
  ${typography.bodyTextSmall};
  text-align: left;
  display: none;

  ${mediaQueries.forDesktopUp} {
    display: block;
  }
`;

export const CTA = styled(Button)`
  min-width: 150px;
  margin-top: ${size.md}px;
  text-align: center;

  ${mediaQueries.forTabletHorizontalUp} {
    margin-top: ${size.xl1}px;
  }
`;

export const CtaWrapper = styled.div`
  width: 100%;

  ${mediaQueries.forTabletHorizontalUp} {
    width: fit-content;
  }
`;

type RowContainerProps = {
  hasOnlyOneImage: boolean;
};
export const RowContainer = styled(Row)<RowContainerProps>`
  justify-content: space-between;
  display: flex;
  ${mediaQueries.forTabletHorizontalUp} {
    padding-top: ${(props) => (props.hasOnlyOneImage ? `${size.xl1}px` : 0)};
  }
`;
